import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../lib/api';
import { CAPA_DE_SECCION } from '../lib/vestidoDelCarril';
import { cuandoTexto } from '../lib/avanceDelGrupo';

/* t7-2a (C1, #516; diseno seccion 1 y 3.4) -- EL RECUADRO "AVANCE DEL GRUPO" DE LA PAGINA DEL GRUPO.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Arriba de todo, con la capa de seccion del profe y SIN plegarse: guia naranja de 3 x 44, el
   titulo, la linea "x de y alumnos han practicado . n cards publicadas . Ultima practica:
   cuando" (sin cards: "Todavia no has publicado ninguna card."), la nota gris de que solo
   cuenta la practica en Estudio Adaptativo y el boton naranja "Ver avance" (44 px, radio 10)
   que lleva a la pagina del avance, con o sin cards.

   t7-3 remate 8 (#591, C97 y C100): la linea dice ahora las publicaciones ACTIVAS y, si las hay,
   las caducadas, como el subtitulo del panel ("10 de 12 alumnos han practicado, 2 publicaciones
   activas, 1 caducada. Ultima practica: hace 7 dias."), y cierra con punto final. Un servidor que
   no manda `cards_activas` deja la linea de antes.

   SIN BRINCO: los datos vienen en la MISMA respuesta que la pagina ya pide al montar
   (`GET /groups/{id}` trae `avance_resumen`). Si no vinieran (un servidor anterior, o el resumen
   fallo), se pide UNA vez `GET /grupos/{id}/avance/resumen`; mientras, la linea no se pinta.

   t7-2a (E3, #519): DEBAJO DEL RECUADRO, la linea del correo del domingo: "Reporte semanal por
   correo: encendido . Apagar" (o "apagado . Encender"), 12 px gris con el verbo como enlace
   naranja. El estado viene en la misma respuesta (`group.reporte_semanal`; ausente = encendido)
   y el verbo lo cambia con `PATCH /grupos/{id}/reporte-semanal`. */

export function LineaDelReporteSemanal({ groupId, encendido: inicial }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [encendido, setEncendido] = useState(inicial !== false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => { setEncendido(inicial !== false); }, [inicial]);

  const cambiar = async () => {
    if (enviando) return;
    setEnviando(true);
    try {
      const { data } = await api.patch(`/grupos/${groupId}/reporte-semanal`, { encendido: !encendido });
      setEncendido(data ? data.reporte_semanal !== false : !encendido);
    } catch (err) {
      // t7-3 remate 7 (regla 7 de #581): "Error de red" solo sin respuesta; con respuesta, el aviso general (o el freno)
      toast.error(t(!err.response ? 'common:errors.network'
        : (err.response.status === 429 ? 'common:errors.rate_limit_generic' : 'common:toasts.generic_error')),
        { id: 'reporte-semanal' });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <p className="mt-2" style={{ fontSize: 12, color: '#a0a0a0' }} data-testid="group-reporte-semanal">
      {t(encendido ? 'groups.avance.reporte_encendido' : 'groups.avance.reporte_apagado')}
      {'. '}
      <button type="button" onClick={cambiar} disabled={enviando}
        className="text-[#FF6B00] hover:underline py-[13px] -my-[13px]"
        // t7-3 remate 7 (regla 2 de #581): 4 px de cada lado hacia afuera: el area de toque llega a 44 de ancho sin moverse
        style={{ fontSize: 12, paddingLeft: 4, paddingRight: 4, marginLeft: -4, marginRight: -4 }}
        data-testid="group-reporte-semanal-cambiar">
        {t(encendido ? 'groups.avance.apagar' : 'groups.avance.encender')}
      </button>
    </p>
  );
}

/** t7-3 (#556, regla 22): la linea del recuadro sin punto medio. Los datos del grupo van con coma
    ("8 de 12 alumnos han practicado, 2 cards publicadas") y lo que es su propia frase, con punto y
    espacio (". Ultima practica: ayer"); si la primera pieza ya es una frase con su punto, solo el espacio. */
export function lineaDelRecuadro(alumnos, cards) {
  const [primera, ...resto] = alumnos;
  const cabeza = /[.!?]$/.test(primera) ? `${primera} ${cards}` : `${primera}, ${cards}`;
  return resto.length ? `${cabeza}. ${resto.join('. ')}` : cabeza;
}

/** t7-3 remate 8 (#591, C97 y C100): la pieza de las publicaciones, igual que en el subtitulo del panel: "2
    publicaciones activas" y ", 1 caducada" cuando las hay (las caducadas del servidor mandan; si no vienen, la
    resta); sin activas, "ninguna publicacion activa". Si la pieza va despues de una frase con su punto (la de
    "sin alumnos" o "nadie ha practicado"), arranca con mayuscula. */
export function publicacionesDelRecuadro(t, g, primera) {
  const activas = Number(g.cards_activas) || 0;
  const caducadas = g.cards_caducadas ?? Math.max(0, (g.cards_publicadas || 0) - activas);
  const piezas = [activas > 0
    ? t('groups.avance.sub.activas', { count: activas })
    : t('groups.avance.sub.activas_ninguna')];
  if (caducadas > 0) piezas.push(t('groups.avance.sub.caducadas', { count: caducadas }));
  const texto = piezas.join(', ');
  return /[.!?]$/.test(primera || '') ? texto.charAt(0).toUpperCase() + texto.slice(1) : texto;
}

/** t7-3 remate 8 (#591, C100): la linea del recuadro termina con punto final (si ya lo trae, no se duplica). */
export function conPuntoFinal(texto) {
  return /[.!?]$/.test(texto) ? texto : `${texto}.`;
}

export default function AvanceDelGrupoRecuadro({ groupId, resumen, reporteSemanal }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const [propio, setPropio] = useState(null);
  const datos = resumen || propio;

  useEffect(() => {
    if (resumen) return undefined;
    let vivo = true;
    api.get(`/grupos/${groupId}/avance/resumen`)
      .then((res) => { if (vivo) setPropio(res.data); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [groupId, resumen]);

  let linea = null;
  if (datos && datos.grupo) {
    const g = datos.grupo;
    // t7-2d (regla del cero, v6 seccion 2.8): sin alumnos o sin nadie practicando, la linea no
    // escribe "0 de y": dice que no hay alumnos o "Nadie ha practicado todavia." (sin "nunca")
    let alumnos;
    if (!g.alumnos) alumnos = [t('groups.avance.sin_alumnos')];
    else if (!datos.practicaron) alumnos = [t('groups.avance.nadie_todavia')];
    else {
      // t7-3 remate 7 (pasada 3, regla 5): el verbo concuerda con los que practicaron, no con el grupo ("1 de 12 alumnos
      // ha practicado"; antes "han")
      const entrada = datos.practicaron === 1 && g.alumnos > 1 ? 'groups.avance.entrada_alumnos_uno' : 'groups.avance.entrada_alumnos';
      alumnos = [t(entrada, { x: datos.practicaron, count: g.alumnos }),
        t('groups.avance.ultima_practica', { cuando: cuandoTexto(t, datos.ultima_practica) })];
    }
    if (g.cards_publicadas === 0) linea = t('groups.avance.sin_cards_corto');
    else if (g.cards_activas === undefined || g.cards_activas === null) {
      // t7-3 remate 2 (R1): "2 publicaciones" (la llave `cards_publicadas` la sigue usando el banco).
      // t7-3 remate 8 (#591, C100): un servidor que no manda `cards_activas` deja la linea de antes, byte a byte
      linea = lineaDelRecuadro(alumnos, t('groups.avance.publicaciones', { count: g.cards_publicadas }));
    } else {
      // t7-3 remate 8 (#591, C97 y C100): las mismas piezas que el subtitulo del panel (las ACTIVAS y, si las hay,
      // las caducadas; sin activas, "ninguna publicacion activa") y la linea cierra con su punto final
      linea = conPuntoFinal(lineaDelRecuadro(alumnos, publicacionesDelRecuadro(t, g, alumnos[0])));
    }
  }

  return (
    <div>
      <div className={`${CAPA_DE_SECCION} flex flex-wrap items-center gap-3`} style={{ padding: 16 }}
        data-testid="group-avance-recuadro">
        <span aria-hidden="true" className="shrink-0"
          style={{ width: 3, height: 44, borderRadius: 2, background: '#FF6B00' }} data-testid="group-avance-guia" />
        <div className="flex-1 min-w-[200px]">
          <p style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{t('groups.avance.titulo')}</p>
          {linea && (
            <p className="mt-0.5" style={{ fontSize: 14, color: '#a0a0a0' }} data-testid="group-avance-linea">{linea}</p>
          )}
          <p className="mt-0.5" style={{ fontSize: 12, color: '#a0a0a0' }}>{t('groups.avance.solo_adaptativo')}</p>
        </div>
        {/* post-t7-2b (remate de D1 y D7): con el resumen a la mano, el grupo (nombre y conteos) viaja en el estado de la
            ruta: el esqueleto del panel reserva con el el alto real de su barra y de su subtitulo */}
        <button type="button" onClick={() => (datos && datos.grupo
          ? navigate(`/groups/${groupId}/avance`, { state: { grupoDelAvance: datos.grupo } })
          : navigate(`/groups/${groupId}/avance`))}
          className="shrink-0 bg-[#FF6B00] hover:bg-[#E05A00] text-white"
          style={{ height: 44, borderRadius: 10, fontWeight: 700, fontSize: 14, paddingLeft: 20, paddingRight: 20 }}
          data-testid="group-avance-ver">
          {t('groups.avance.ver_avance')}
        </button>
      </div>
      <LineaDelReporteSemanal groupId={groupId} encendido={reporteSemanal} />
    </div>
  );
}
